import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { increment, serverTimestamp } from 'firebase/database';
import { ParserHlp } from 'src/lib/fliko/helpers/parser.helper';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { ConnectionData, ConnectionNoteData } from 'src/lib/fliko/models/connection/connection.data';
import { ResidentData } from 'src/lib/fliko/models/person/resident.data';
import { Collection } from 'src/lib/fliko/types/database.type';
import { DatabaseService } from 'src/app/shared/services/database.service';
import { NulCol } from 'src/app/shared/services/database/dbCollection';
import { PushIdService } from 'src/app/shared/services/pushId.service';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { ResidentService } from 'src/app/view/users/services/resident.service';

@Injectable({ providedIn: 'root' })
export class ConnectionsService {
  constructor(
    private dbSrv: DatabaseService,
    private ctxDS: ContextDataService,
    private residentService: ResidentService,
    private pushIdService: PushIdService
  ) {}

  /**
   * @path /connectionHistory/$estateId/
   * @key id
   * @database shard
   */
  private get connectionHistory() {
    return this.dbSrv.estateFactory.getCollection<ConnectionData>('connectionHistory/$eId');
  }

  /**
   * @path /connectionHistoryNote/$estateId/
   * @key connectionId
   * @database shard
   */
  private get connectionNotes() {
    return this.dbSrv.estateFactory.getCollection<Collection<ConnectionNoteData>>('connectionHistoryNote/$eId');
  }

  /** Root object used for atomic multi-path writes within the current estate shard. */
  private get shardRoot() {
    return this.dbSrv.estateFactory.getObject<Record<string, unknown>>('');
  }

  public getConnectionHistory$(): Observable<ConnectionData[]> {
    return this.connectionHistory.getList();
  }

  public getConnectionNotes$(): Observable<NulCol<Collection<ConnectionNoteData>>> {
    return this.connectionNotes.getObject();
  }

  public async saveNote(connectionId: string, content: string): Promise<void> {
    const trimmedContent = content.trim();
    if (!trimmedContent.length) return;

    const estateId = this.ctxDS.peekCurrentEstateId();
    const timestamp = serverTimestamp();
    const currentUser = this.ctxDS.peekCurrentUser();
    const currentUserId = this.ctxDS.peekCurrentUserId() || null;
    const authorName = ToString.fullName(currentUser) || currentUser?.email || currentUserId || 'System';
    const noteId = this.pushIdService.create();
    const note = {
      id: noteId,
      connectionId,
      authorUserId: currentUserId,
      authorName,
      content: trimmedContent,
      createdAt: timestamp,
      lastUpdate: timestamp
    };

    await this.shardRoot.update({
      [`connectionHistoryNote/${estateId}/${connectionId}/${noteId}`]: note,
      [`connectionHistory/${estateId}/${connectionId}/notesCount`]: increment(1),
      [`connectionHistory/${estateId}/${connectionId}/lastNoteAt`]: timestamp,
      [`connectionHistory/${estateId}/${connectionId}/lastUpdate`]: timestamp
    });
  }

  public async assignResident(connectionId: string, resident: ResidentData, phoneNumber: string): Promise<void> {
    const timestamp = Date.now();
    const residentName = ToString.fullName(resident);
    const nextPhones = this.mergePhones(resident.phone, phoneNumber);
    const residentPhoneChanged = !this.haveSamePhones(resident.phone, nextPhones);
    const tasks: Promise<void>[] = [
      this.connectionHistory.updateElement(connectionId, {
        estateId: resident.estateId,
        residentId: resident.id,
        residentName,
        lastUpdate: timestamp
      })
    ];

    if (residentPhoneChanged) {
      tasks.push(
        this.residentService.updateResident({
          ...resident,
          phone: nextPhones.length ? nextPhones : null,
          lastUpdate: timestamp
        })
      );
    }

    await Promise.all(tasks);
  }

  private mergePhones(existingPhones: string[] | null, phoneNumber: string): string[] {
    const nextPhoneSan = ParserHlp.sanitizeNumber(phoneNumber);
    if (!nextPhoneSan) return existingPhones ?? [];

    const phones = [...(existingPhones ?? [])];
    const alreadyAssigned = phones.some((phone) => ParserHlp.sanitizeNumber(phone) === nextPhoneSan);
    if (!alreadyAssigned) phones.push(phoneNumber);
    return phones;
  }

  private haveSamePhones(currentPhones: string[] | null, nextPhones: string[]): boolean {
    if ((currentPhones ?? []).length !== nextPhones.length) return false;
    return nextPhones.every((phone, index) => phone === currentPhones?.[index]);
  }
}
